import { Controller } from "stimulus"
import IMask from "imask"
import sanitizeHtml from "sanitize-html"
import { validateSsn } from "validators/ssn_validator"
import { validateCitizenship } from "validators/citizenship_validator"
import { validateTribal } from "validators/tribal_validator"
import { applyResult, removeFieldErrorState, finalizeForm } from "validators/validation_display"
export default class extends Controller {
  static targets = [
    "TribalContainer",
    "FeaturedTribeContainer",
    "TribalNameContainer",
    "TribalName",
    "TribalState",
    "ConsumerFields",
    "SsnMessage",
    "SsnInput",
    "NoSsnCheckbox",
    "RelationshipFields",
    "DependentAddress",
    "NaturalizedCitizenContainer",
    "ImmigrationStatusContainer",
    "AddressContainer",
    "addressFields",
    "newAddressFields",
    "NewMailingAddressFieldsTemplate",
    "NewHomeAddressFieldsTemplate",
    "EligibleImmigrationStatusContainer",
    "UsCitizenshipFields",
    "IncarceratedFields",
    "IndianTribeMember",
    "AddressButtons",
    "destroyMailingAddress"
  ]

  connect() {
    this.initializeApplyingCoverage()
    this.initializeCitizenshipFields()
    this.maskSSN()
    this.maskZip()
    this.initializeRequiredFields()
    this.initializeTribalFields()
    // using existing init_glossary function for now to be consistent
    init_glossary();
    // when the accessible-form-validation flag is on, errors are shown inline (not via native
    // tooltips), so clear a field's inline error as soon as the user edits/answers it.
    if (this.#accessibleValidationEnabled()) this.#setupAccessibleErrorClearing()
  }

  disconnect() {
    if (this._fieldEditHandler) {
      this.element.removeEventListener('input', this._fieldEditHandler)
      this.element.removeEventListener('change', this._fieldEditHandler)
      this._fieldEditHandler = null
    }
  }

  // true when the global accessible-form-validation flag is on (rendered on <html> by the layout).
  #accessibleValidationEnabled() {
    return document.documentElement.dataset.accessibleFormValidation === 'true'
  }

  // clear field's inline error the moment the user edits it (text/select) or answers it (radio).
  // tribe-code checkboxes get the dedicated group clear
  // mirrors the consumer form-validation controller
  #setupAccessibleErrorClearing() {
    this._fieldEditHandler = (event) => {
      const field = event.target
      if (!field || !field.matches) return
      if (field.classList.contains('tribe_codes')) { this.#clearTribeCodesError(); return }
      if (field.matches('input, select, textarea')) removeFieldErrorState(field)
    }
    this.element.addEventListener('input', this._fieldEditHandler)
    this.element.addEventListener('change', this._fieldEditHandler)
  }

  initializeTribalFields() {
    const indianTribeMemberYes = document.getElementById('indian_tribe_member_yes')
    if (indianTribeMemberYes?.checked) {
      this.showTribalFields()
      this.setTribalFieldsRequired(true)
    } else {
      if (this.hasTribalNameTarget) {
        this.setTribalNameRequired(false)
      }
      this.setTribalFieldsRequired(false)
    }
  }

  toggleTribalFields(event) {
    const isYes = event.target.value === 'true'
    if (isYes) {
      this.showTribalFields()
      this.setTribalFieldsRequired(true)
    } else {
      this.hideTribalFields()
      this.clearTribalFields()
      this.setTribalFieldsRequired(false)
    }
  }

  showTribalFields() {
    this.TribalContainerTarget.classList.remove('hide');
    const enrollStateAbbr = this.element.querySelector('#enroll_state_abbr').value
    const isFeaturedTribesEnabled = this.element.querySelector('#is_featured_tribes_selection_enabled').value === 'true'
    const selectedState = this.TribalStateTarget?.value
    if (isFeaturedTribesEnabled && selectedState === enrollStateAbbr) {
      this.TribalNameContainerTarget.classList.remove('hide')
      if (this.hasFeaturedTribeContainerTarget) {
        this.FeaturedTribeContainerTarget.classList.remove('hide')
      }
      this.toggleOtherTribeName()
    } else {
      this.TribalNameContainerTarget.classList.remove('hide')
      if (this.hasFeaturedTribeContainerTarget) {
        this.FeaturedTribeContainerTarget.classList.add('hide')
      }
      this.setTribalNameRequired(true)
    }
  }

  hideTribalFields() {
    this.TribalContainerTarget.classList.add('hide')
  }

  clearTribalFields() {
    if (this.hasTribalStateTarget) {
      this.TribalStateTarget.value = ''
    }

    if (this.hasTribalNameTarget) {
      this.TribalNameTarget.value = ''
    }

    const tribeCheckboxes = this.element.querySelectorAll('.tribe_codes:checked')
    tribeCheckboxes.forEach(checkbox => checkbox.checked = false)
  }

  handleTribalStateChange(event) {
    const enrollStateAbbr = this.element.querySelector('#enroll_state_abbr').value
    const isFeaturedTribesEnabled = this.element.querySelector('#is_featured_tribes_selection_enabled').value === 'true'
    const isTribalDetailsEnabled = this.element.querySelector('#is_indian_alaskan_tribe_details_enabled').value === 'true'
    if (!isTribalDetailsEnabled) return

    const selectedState = event.target.value
    if (isFeaturedTribesEnabled && selectedState === enrollStateAbbr) {
      this.FeaturedTribeContainerTarget.classList.remove('hide')
      this.toggleOtherTribeName()
    } else {
      const tribeCheckboxes = this.element.querySelectorAll('.tribe_codes:checked')
      tribeCheckboxes.forEach(checkbox => checkbox.checked = false)

      this.TribalNameContainerTarget.classList.remove('hide')
      this.FeaturedTribeContainerTarget.classList.add('hide')
      this.setTribalNameRequired(true)
    }

    // Hide any existing alerts
    const tribalStateAlert = document.getElementById('tribal-state-alert')
    const tribalNameAlert = document.getElementById('tribal-name-alert')
    if (tribalStateAlert) tribalStateAlert.classList.add('hide')
    if (tribalNameAlert) tribalNameAlert.classList.add('hide')
  }

  setTribalFieldsRequired(required) {
    const input = this.element.querySelector('#is_indian_alaskan_tribe_details_enabled')
    if (!input) return
    const isTribalDetailsEnabled = input.value === 'true'
    if (isTribalDetailsEnabled) {
      // Set tribal state as required
      if (this.hasTribalStateTarget) {
        if (required) {
          this.TribalStateTarget.setAttribute('required', 'required')
        } else {
          this.TribalStateTarget.removeAttribute('required')
        }
      }

      // Handle tribal name requirement based on state selection and featured tribes
      const selectedState = this.TribalStateTarget?.value
      const enrollStateAbbr = this.element.querySelector('#enroll_state_abbr').value
      const isFeaturedTribesEnabled = this.element.querySelector('#is_featured_tribes_selection_enabled').value === 'true'

      if (isFeaturedTribesEnabled && selectedState === enrollStateAbbr) {
        // For featured tribes, only require tribal name if "Other" is selected
        const otherTribeSelected = this.element.querySelector('input#person_tribe_codes_ot')?.checked
        this.setTribalNameRequired(required && otherTribeSelected)
      } else {
        // Always require tribal name for non-featured tribe states
        this.setTribalNameRequired(required)
      }
    } else {
      // Handle tribal ID requirement for non-tribal-details case
      const tribalId = this.element.querySelector('#tribal-id')
      if (tribalId) {
        if (required) {
          tribalId.setAttribute('required', 'required')
        } else {
          tribalId.removeAttribute('required')
        }
      }
    }
  }

  setTribalNameRequired(required) {
    if (this.hasTribalNameTarget) {
      if (required) {
        this.TribalNameTarget.setAttribute('required', 'required')
      } else {
        this.TribalNameTarget.removeAttribute('required')
      }
    }
  }

  toggleOtherTribeName() {
    let input = document.querySelector('input#applicant_demographics_attributes_tribe_codes_ot')
    let tribalCodes = document.querySelectorAll('.tribe_codes')
    if (input.checked) {
      this.TribalNameContainerTarget.classList.remove('hide')
      this.setTribalNameRequired(true)
      tribalCodes.forEach(code => {
        code.classList.remove("indicate-invalid")
        code.setCustomValidity("")
        code.reportValidity()
      })
    } else {
      this.TribalNameContainerTarget.classList.add('hide')
      this.setTribalNameRequired(false)
      this.TribalNameTarget.value = ''
    }

    // Check if any tribe is selected when featured tribes are enabled
    const isFeaturedTribesEnabled = this.element.querySelector('#is_featured_tribes_selection_enabled').value === 'true'
    if (isFeaturedTribesEnabled) {
      const anyTribeSelected = Array.from(this.element.querySelectorAll('.tribe_codes'))
        .some(checkbox => checkbox.checked)

      if (!anyTribeSelected && !this.#accessibleValidationEnabled()) {
        // show validation message if no tribe is selected (legacy path only — the accessible path
        // surfaces this as inline invalid-feedback at submit instead of the alert div).
        const tribalNameAlert = document.getElementById('tribal-name-alert')
        if (tribalNameAlert) tribalNameAlert.classList.remove('hide')
      } else {
        tribalCodes.forEach(code => {
          code.classList.remove("indicate-invalid")
          code.setCustomValidity("")
          code.reportValidity()
        })
      }
    }
  }

  initializeApplyingCoverage() {
    const applyingCoverageRadios = this.element.querySelectorAll('input[name="applicant[is_applying_coverage]"]')
    if (applyingCoverageRadios.length > 0) {
      this.addNoSsnListener()
      this.addSsnInputListener()

      // Check initial state
      const notApplyingForCoverage = Array.from(applyingCoverageRadios)
        .find(radio => !radio.checked && radio.value === 'true')

      if (notApplyingForCoverage) {
        this.hideConsumerFields()
        this.checkSsnMessage()
      }
    }
  }

  toggleApplyingCoverage(event) {
    const applyingForCoverage = event.target.value === 'true'
    if (!applyingForCoverage) {
      this.hideConsumerFields()
      this.checkSsnMessage()
      this.setUsCitizenshipRequired(false)
      this.setNaturalizedCitizenRequired(false)
      this.setIncarceratedRequired(false)
    } else {
      this.showConsumerFields()
      this.hideSsnMessage()
      this.setUsCitizenshipRequired(true)
      this.setIncarceratedRequired(true)

      const isUsCitizen = this.element.querySelector('#us_citizen_true')?.checked
      if (isUsCitizen) {
        this.setNaturalizedCitizenRequired(true)
      }
    }
  }

  hideConsumerFields() {
    if (this.hasConsumerFieldsTarget) {
      this.ConsumerFieldsTargets.forEach(field => {
        field.classList.add('hide')
      })
    }
  }

  showConsumerFields() {
    if (this.hasConsumerFieldsTarget) {
      this.ConsumerFieldsTargets.forEach(field => {
        field.classList.remove('hide')
      })
    }
  }

  showSsnMessage() {
    if (this.hasSsnMessageTarget) {
      this.SsnMessageTarget.classList.remove('hide')
    }
  }

  hideSsnMessage() {
    if (this.hasSsnMessageTarget) {
      this.SsnMessageTarget.classList.add('hide')
    }
  }

  checkSsnMessage() {
    if (this.hasSsnInputTarget && this.hasNoSsnCheckboxTarget) {
      if (this.SsnInputTarget.value === '' && !this.NoSsnCheckboxTarget.checked) {
        this.showSsnMessage()
      }
    }
  }

  handleNoSsnChange(event) {
    if (event.target.checked) {
      this.hideSsnMessage()
    } else {
      this.checkSsnMessage()
    }
  }

  handleSsnInput(event) {
    if (event.target.value !== '') {
      this.hideSsnMessage()
    } else {
      this.checkSsnMessage()
    }
  }

  addNoSsnListener() {
    if (this.hasNoSsnCheckboxTarget) {
      this.NoSsnCheckboxTarget.addEventListener('change', this.handleNoSsnChange.bind(this))
    }
  }

  addSsnInputListener() {
    if (this.hasSsnInputTarget) {
      this.SsnInputTarget.addEventListener('keyup', this.handleSsnInput.bind(this))
    }
  }

  toggleDependentAddress(event) {
    if (this.hasAddressFieldsTarget) {
      if (event.target.checked) {
        this.addressFieldsTarget.innerHTML = ''
        this.AddressButtonsTarget.classList.add('hide')
      } else {
        this.addressFieldsTarget.innerHTML = ''
        this.addressFieldsTarget.insertAdjacentHTML('beforeend', this.sanitize(this.NewHomeAddressFieldsTemplateTarget.innerHTML))
        this.AddressButtonsTarget.classList.remove('hide')
        this.maskZip()
      }
    }
  }

  hideAddressFields(formElement) {
    const addressFields = formElement.querySelector('[data-target="family-information.AddressFields"]')
    if (addressFields) {
      addressFields.classList.add('hide')
    }
  }

  showAddressFields(formElement) {
    const addressFields = formElement.querySelector('[data-target="family-information.AddressFields"]')
    if (addressFields) {
      addressFields.classList.remove('hide')
      this.maskZip()
    }
  }

  displayImmigrationFieldWarning() {
    // is applying for coverage
    // if not us citizen and not have eligible immigration status checked
    // or if immigration status container is visible and fields left blank
    const warning = document.getElementById('immigrationInfoWarning')
    if (warning && warning.classList.contains('hidden')) {
      const isApplyingCoverage = this.element.querySelector('#applicant_is_applying_coverage_true')?.checked
      const isUsCitizen = this.element.querySelector('#us_citizen_true')?.checked
      const isNaturalizedCitizen = this.element.querySelector('#naturalized_citizen_true')?.checked

      if (isApplyingCoverage && (!isUsCitizen || isNaturalizedCitizen)) {
        const eligibleImmigrationStatus = this.element.querySelector('#eligible_immigration_status_true')?.checked
        if (eligibleImmigrationStatus) {
          let immigrationStatusEmpty = this.emptyImmigrationStatusFields()
          if (immigrationStatusEmpty) {
            warning.classList.remove('hidden')
            return true
          } else {
            warning.classList.add('hidden')
            return false
          }
        } else {
          warning.classList.remove('hidden')
          return true
        }
      } else {
        warning.classList.add('hidden')
        return false
      }
    } else {
      return false
    }
  }

  emptyImmigrationStatusFields() {
    const immigrationStatusFields = this.element.querySelectorAll('#immigration-checkbox-container select, #immigration-checkbox-container input')
    return immigrationStatusFields.any(field => field.value == '' || field.value == null)
  }

  toggleCitizenshipFields(event) {
    const isUsCitizen = event.target.value === 'true';

    if (this.hasNaturalizedCitizenContainerTarget && this.hasEligibleImmigrationStatusContainerTarget) {
      if (isUsCitizen) {
        // Show naturalized citizen section and hide immigration status
        this.NaturalizedCitizenContainerTarget.classList.remove('hidden')
        this.EligibleImmigrationStatusContainerTarget.classList.add('hidden')

        // Make naturalized citizen radios required if applying for coverage
        const isApplyingCoverage = this.element.querySelector('#applicant_is_applying_coverage_true')?.checked
        if (isApplyingCoverage) {
          this.setNaturalizedCitizenRequired(true)
        }
      } else {
        // Hide naturalized citizen section and show immigration status
        this.NaturalizedCitizenContainerTarget.classList.add('hidden')
        this.EligibleImmigrationStatusContainerTarget.classList.remove('hidden')

        // Remove required from naturalized citizen radios
        this.setNaturalizedCitizenRequired(false)

        // Clear the naturalized citizen selection
        const naturalizedRadios = this.NaturalizedCitizenContainerTarget.querySelectorAll('input[type="radio"]')
        naturalizedRadios.forEach(radio => radio.checked = false)
      }
    }
  }

  addMailingAddress(event) {
    event.preventDefault()
    this.newAddressFieldsTarget.innerHTML = ''
    this.newAddressFieldsTarget.insertAdjacentHTML('beforeend', this.sanitize(this.NewMailingAddressFieldsTemplateTarget.innerHTML))
    document.getElementById('add_mail_address').classList.add('d-none')
    document.getElementById('remove_mail_address').classList.remove('d-none')
    this.maskZip()
  }

  removeMailingAddress(event) {
    event.preventDefault()
    // Clear any content in the NewAddressFields target
    if (this.newAddressFieldsTarget.firstChild) {
      this.newAddressFieldsTarget.removeChild(this.newAddressFieldsTarget.firstChild)
    }
    this.newAddressFieldsTarget.innerHTML = ''

    if (this.hasDestroyMailingAddressTarget) {
      this.destroyMailingAddressTarget.value = 'true'
    }

    // Toggle button visibility
    document.getElementById('remove_mail_address').classList.add('d-none')
    document.getElementById('add_mail_address').classList.remove('d-none')
  }

maskSSN() {
  const ssnFields = this.element.querySelectorAll('.mask-ssn')
  ssnFields.forEach(field => {
    const mask = IMask(field, { mask: "000-00-0000" })
    mask.on('accept', () => {
      if (mask.value !== '' && this.hasNoSsnCheckboxTarget) {
        this.NoSsnCheckboxTarget.checked = false
        field.removeAttribute('disabled')
      }
      // Re-enable Save button whenever user edits SSN field
      const submitBtn = this.element.querySelector('[data-action*="checkValidations"]')
      if (submitBtn) {
        submitBtn.classList.remove('disabled')
        submitBtn.setAttribute('tabindex', 0)
        submitBtn.removeAttribute('disabled')
      }
    })
  })
}
  maskZip() {
    const zipFields = this.element.querySelectorAll('.zip')
    zipFields.forEach(field => {
      IMask(field, { mask: "00000" });
    })
  }

  initializeCitizenshipFields() {
    if (this.hasNaturalizedCitizenContainerTarget && this.hasEligibleImmigrationStatusContainerTarget) {
      const usCitizenTrue = this.element.querySelector('#us_citizen_true')
      const usCitizenFalse = this.element.querySelector('#us_citizen_false')

      // Initially hide both sections until US citizen is answered
      if (!usCitizenTrue?.checked && !usCitizenFalse?.checked) {
        this.NaturalizedCitizenContainerTarget.classList.add('hidden')
        this.EligibleImmigrationStatusContainerTarget.classList.add('hidden')
        return
      }

      // Handle case where US citizen is already selected
      if (usCitizenTrue?.checked) {
        this.NaturalizedCitizenContainerTarget.classList.remove('hidden')
        this.EligibleImmigrationStatusContainerTarget.classList.add('hidden')

        // Make naturalized citizen radios required if applying for coverage
        const isApplyingCoverage = this.element.querySelector('#applicant_is_applying_coverage_true')?.checked
        if (isApplyingCoverage) {
          this.setNaturalizedCitizenRequired(true)
        }
      }
      // Handle case where Not US citizen is already selected
      else if (usCitizenFalse?.checked) {
        this.NaturalizedCitizenContainerTarget.classList.add('hidden')
        this.EligibleImmigrationStatusContainerTarget.classList.remove('hidden')
      }
    }
  }

  initializeRequiredFields() {
    const applyingCoverageRadio = this.element.querySelector('#applicant_is_applying_coverage_true')
    const isApplyingCoverage = applyingCoverageRadio?.checked
    const isUsCitizen = this.element.querySelector('#us_citizen_true')?.checked



    if (isApplyingCoverage) {
      this.setUsCitizenshipRequired(true)
      this.setIncarceratedRequired(true)

      if (isUsCitizen) {
        this.setNaturalizedCitizenRequired(true)
      }
    } else {
      this.setUsCitizenshipRequired(false)
      this.setNaturalizedCitizenRequired(false)
      this.setIncarceratedRequired(false)
    }
  }

  setUsCitizenshipRequired(required) {
    if (this.hasUsCitizenshipFieldsTarget) {
      const usCitizenRadios = this.UsCitizenshipFieldsTarget.querySelectorAll('input[type="radio"]')
      usCitizenRadios.forEach(radio => {
        if (required) {
          radio.setAttribute('required', 'required')
        } else {
          radio.removeAttribute('required')
        }
      })
    }
  }

  setNaturalizedCitizenRequired(required) {
    if (this.hasNaturalizedCitizenContainerTarget) {
      const naturalizedRadios = this.NaturalizedCitizenContainerTarget.querySelectorAll('input[type="radio"]')
      naturalizedRadios.forEach(radio => {
        if (required) {
          radio.setAttribute('required', 'required')
        } else {
          radio.removeAttribute('required')
        }
      })
    }
  }

  setIncarceratedRequired(required) {
    if (this.hasIncarceratedFieldsTarget) {
      const incarceratedRadios = this.IncarceratedFieldsTarget.querySelectorAll('input[type="radio"]')
      incarceratedRadios.forEach(radio => {
        if (required) {
          radio.setAttribute('required', 'required')
        } else {
          radio.removeAttribute('required')
        }
      })
    }
  }

  toggleNoSsn(event) {
    let input = this.SsnInputTarget.querySelector('input')
    let container = this.SsnInputTarget.querySelector('#ssn-input-container')
    let facade = this.SsnInputTarget.querySelector('.ssn-facade')
    let closeEye = this.SsnInputTarget.querySelector('img[class^=ssn-eye-off-]')
    let openEye = this.SsnInputTarget.querySelector('img[class^=ssn-eye-on-]')
    if (event.target.checked) {
      if (facade) {
        facade.classList.add('hidden')
        facade.innerHTML = ''
        if (input) {
          input.classList.add('hidden')
          input.value = ''
        }
      } else {
        if (input) {
          input.value = ''
        }
      }
      if (closeEye) {
        closeEye.classList.add('hidden')
      }
      if (container) {
        container.classList.add('div-disabled')
      }

      if (openEye) {
        openEye.classList.add('hidden')
      }
    } else {
      if (facade) {
        facade.classList.remove('hidden')
        if (input) {
          input.classList.remove('hidden')
          input.removeAttribute('disabled')
        }
      } else {
        if (input) {
          input.removeAttribute('disabled')
        }
      }
      if (closeEye) {
        closeEye.classList.remove('hidden')
      }
      if (container) {
        container.classList.remove('div-disabled')
      }
      if (input) {
        input.classList.remove('hidden')
      }
    }

    // accessible flow -> checking "No SSN" satisfies the SSN-required rule, so clear SSN field error once the form has been validated.
    this.#clearSsnErrorOnNoSsn(event.target, input)
  }

  // similar method present in the consumer form-validation controller to clear the SSN field's inline error when "No SSN" is checked
  #clearSsnErrorOnNoSsn(checkbox, input) {
    if (!this.#accessibleValidationEnabled()) return
    if (!checkbox.checked) return
    const form = this.element.querySelector('form')
    if (!form?.classList.contains('was-validated')) return
    if (input && !input.checkValidity()) removeFieldErrorState(input)
  }

  ssnValueChanged(event) {
    if (event.target.value !== '') {
      this.NoSsnCheckboxTarget.checked = false
    }
  }

  checkValidations(event) {
    // w/accessible form flag on: use the shared validators + inline invalid-feedback display (no native tooltips)
    if (this.#accessibleValidationEnabled()) {
      this.#checkValidationsAccessible(event)
      return
    }

    event.preventDefault()
    event.target.classList.add('disabled');
    event.target.setAttribute('tabindex', -1);
    event.target.blur();
    event.target.setAttribute('disabled', 'disabled');

    const form = this.element.querySelector('form')
    let ssnValid = this.checkSsnValidation()
    let tribalStateValid = this.checkTribalStateValidation()

    let valid = ssnValid && tribalStateValid && form.checkValidity()
    let immigrationFieldsNotFilled = this.displayImmigrationFieldWarning()

    if (valid && !immigrationFieldsNotFilled) {
      form.submit()
    } else {
      event.target.classList.remove('disabled');
      event.target.setAttribute('tabindex', 0);
      event.target.removeAttribute('disabled');
      if (immigrationFieldsNotFilled) {
        //show warning and scroll to warning
        const warning = document.getElementById('immigrationInfoWarning')
        if (warning) {
          warning.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }
      form.reportValidity()
    }
  }

  // --- Accessible (flag-on) validation path -------------------------------------------------
  // NOTE: many of the fields below (or slight variations thereof) are also present in the consumer form-validation controller,
  // but we duplicate here to keep the accessible flow's inline error-handling self-contained within this controller (since the consumer controller still uses native tooltips)
  // ideally, once the accessible validation path is fully adopted we can consolidate the shared validation logic into a common module that both controllers call into
  // but for now this minimizes risk to the legacy flow

  // mirror of checkValidations for the accessible flow: cross-field rules drive inline
  // invalid-feedback (via setCustomValidity), then finalizeForm surfaces every invalid field and
  // scrolls/focuses the first one. The immigration reminder banner is retained.
  #checkValidationsAccessible(event) {
    event.preventDefault()
    const button = event.target
    button.classList.add('disabled')
    button.setAttribute('tabindex', -1)
    button.blur()
    button.setAttribute('disabled', 'disabled')

    const form = this.element.querySelector('form')

    // these set custom validity, so they must run before finalizeForm()'s checkValidity() snapshot
    this.#validateSsnAccessible()
    this.#validateConsumerFieldsAccessible()

    const formValid = finalizeForm(form)
    // The immigration reminder only makes sense once the "Is this person a US citizen?" question is
    // answered: an unanswered radio reads the same as "No" to displayImmigrationFieldWarning, so without
    // this guard a blank submit would trip the banner (the original bug). When unanswered, the inline
    // citizenship error covers it instead. A naturalized citizen who leaves their documents blank still
    // gets the banner, since citizenship is answered.
    const citizenshipAnswered = this.#radioBool('us_citizen_true', 'us_citizen_false') !== null
    const immigrationFieldsNotFilled = citizenshipAnswered ? this.displayImmigrationFieldWarning() : false

    if (formValid && !immigrationFieldsNotFilled) {
      form.submit()
      return
    }

    // re-enable the Save/Confirm button so the user can correct and resubmit
    button.classList.remove('disabled')
    button.setAttribute('tabindex', 0)
    button.removeAttribute('disabled')

    // when only the immigration documents are missing, finalizeForm found no invalid field to scroll
    // to — surface the retained reminder banner instead.
    if (formValid && immigrationFieldsNotFilled) {
      const warning = document.getElementById('immigrationInfoWarning')
      if (warning) warning.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  // SSN / no-SSN cross-field rule -> inline invalid-feedback (no native tooltip).
  #validateSsnAccessible() {
    if (!this.hasNoSsnCheckboxTarget || !this.hasSsnInputTarget) return
    const input = this.SsnInputTarget.querySelector('input')
    const facade = this.SsnInputTarget.querySelector('.ssn-facade')
    const noSsn = this.NoSsnCheckboxTarget
    if (!input) return

    removeFieldErrorState(input)
    removeFieldErrorState(noSsn)

    const result = validateSsn({
      digits: input.value.replace(/\D/g, ''),
      noSsn: noSsn.checked,
      hasStoredSsn: !!(facade && facade.innerHTML.trim() !== '')
    })
    if (!result.valid) applyResult(result.target === 'noSsn' ? noSsn : input, result)
  }

  // base citizenship (us_citizen + conditional naturalized), incarceration, and tribal rules ->
  // inline invalid-feedback. Only runs when applying for coverage (mirrors the server guard and the
  // consumer form-validation controller). eligible-immigration status keeps its reminder banner.
  #validateConsumerFieldsAccessible() {
    const usCitizenEl = this.element.querySelector('#us_citizen_true')
    const naturalizedEl = this.element.querySelector('#naturalized_citizen_true')
    const incarceratedEl = this.element.querySelector('#is_incarcerated_true')
    const tribeMemberEl = this.element.querySelector('#indian_tribe_member_yes')
    const tribalStateEl = this.hasTribalStateTarget ? this.TribalStateTarget : null
    const tribalNameEl = this.hasTribalNameTarget ? this.TribalNameTarget : null

    // reset every field here before re-evaluating
    ;[usCitizenEl, naturalizedEl, incarceratedEl, tribeMemberEl, tribalStateEl, tribalNameEl]
      .forEach(el => removeFieldErrorState(el))
    this.#clearTribeCodesError()

    if (!this.#isApplyingCoverage()) return

    // citizenship — immigrationStatusRequired mirrors
    // EnrollRegistry.feature_enabled?(:immigration_status_question_required), surfaced as a data
    // attribute on the us_citizen_true radio by the view
    // when off, the immigration branch stays on the legacy reminder banner
    validateCitizenship({
      usCitizen: this.#radioBool('us_citizen_true', 'us_citizen_false'),
      naturalized: this.#radioBool('naturalized_citizen_true', 'naturalized_citizen_false'),
      immigrationStatusRequired: usCitizenEl?.dataset.immigrationStatusRequired === 'true'
    }).forEach(result => {
      if (result.valid) return
      applyResult(result.target === 'naturalized' ? naturalizedEl : usCitizenEl, result)
    })

    // incarceration — required radio
    if (incarceratedEl && this.#radioBool('is_incarcerated_true', 'is_incarcerated_false') === null) {
      applyResult(incarceratedEl, { valid: false, target: 'isIncarcerated', code: 'incarceration_required' })
    }

    // tribal — the tribe-member question is required; sub-fields validated when "yes"
    const isTribeMember = this.#radioBool('indian_tribe_member_yes', 'indian_tribe_member_no')
    if (tribeMemberEl && isTribeMember === null) {
      applyResult(tribeMemberEl, { valid: false, target: 'tribeMember', code: 'tribe_member_required' })
    }
    // clear tribal fields when the user answers "no" so they are not validated
    if (isTribeMember === false) this.clearTribalFields()

    const enrollState = this.element.querySelector('#enroll_state_abbr')?.value
    const featuredEnabled = this.element.querySelector('#is_featured_tribes_selection_enabled')?.value === 'true'
    const tribeCodeEls = Array.from(this.element.querySelectorAll('.tribe_codes'))
    validateTribal({
      isTribeMember: isTribeMember === true,
      tribalState: tribalStateEl?.value || '',
      tribalName: tribalNameEl?.value || '',
      featuredTribesInState: featuredEnabled && tribalStateEl?.value === enrollState,
      otherTribeSelected: tribeCodeEls.some(cb => cb.value === 'OT' && cb.checked),
      anyTribeSelected: tribeCodeEls.some(cb => cb.checked)
    }).forEach(result => {
      if (result.valid) return
      if (result.target === 'tribeCodes') this.#applyTribeCodesError(tribeCodeEls)
      else applyResult(result.target === 'tribalName' ? tribalNameEl : tribalStateEl, result)
    })
  }

  // true | false | null (null = neither radio of the pair is selected)
  #radioBool(trueId, falseId) {
    if (this.element.querySelector(`#${trueId}`)?.checked) return true
    if (this.element.querySelector(`#${falseId}`)?.checked) return false
    return null
  }

  #isApplyingCoverage() {
    const applying = this.element.querySelector('#applicant_is_applying_coverage_true')
    return applying ? applying.checked : true
  }

  // The featured-tribe checkboxes are a group; mark it invalid via one box (blocks submit) and reveal
  // the fieldset's invalid-feedback. feedbackFor can't resolve a checkbox group, so handle it here.
  #applyTribeCodesError(tribeCodeEls) {
    if (tribeCodeEls[0]) tribeCodeEls[0].setCustomValidity('invalid')
    const container = this.element.querySelector('.featured-tribe-container')
    container?.classList.add('is-invalid-group')
    const feedback = container?.querySelector('.invalid-feedback')
    if (feedback) feedback.classList.add('d-block')
  }

  #clearTribeCodesError() {
    this.element.querySelectorAll('.tribe_codes').forEach(cb => cb.setCustomValidity(''))

    const container = this.element.querySelector('.featured-tribe-container')
    container?.classList.remove('is-invalid-group')
    const feedback = container?.querySelector('.invalid-feedback')
    if (feedback) feedback.classList.remove('d-block')
  }

  checkSsnValidation() {
    if (this.hasNoSsnCheckboxTarget && this.hasSsnInputTarget) {
      let input = this.SsnInputTarget.querySelector('input')
      let facade = this.SsnInputTarget.querySelector('.ssn-facade')
      let maskedSsn = facade && facade.innerHTML.length > 0
      let noSsn = this.NoSsnCheckboxTarget
      if (input && input.pattern && input.pattern.length > 0) {
        let ssnPattern = input.pattern
        let ssnType = input.type
        if (input && input.classList.contains('hidden')) {
          input.removeAttribute('pattern')
          input.removeAttribute('type')
          input.disabled = true
        } else {
          input.setAttribute('pattern', ssnPattern)
          input.setAttribute('type', ssnType)
          input.disabled = false
        }
      }
      if (this.NoSsnCheckboxTarget.checked && (input.value.length > 1 || maskedSsn)) {
        input.removeAttribute('disabled')
        noSsn.setCustomValidity("Cannot provide an SSN and claim you don't have a SSN")
        noSsn.reportValidity()
        return false
      } else if (!this.NoSsnCheckboxTarget.checked && (input.value.length == 0 && !maskedSsn)) {
        input.removeAttribute('disabled')
        noSsn.setCustomValidity("One of the following is required: SSN, or check the box that you don't have an SSN")
        noSsn.reportValidity()
        return false
      } else {
        input.removeAttribute('disabled')
        noSsn.setCustomValidity("")
        noSsn.reportValidity()
        return true
      }
    } else {
      return true
    }
  }

  checkTribalStateValidation() {
    if (!this.hasTribalStateTarget) return true
    const indianTribeMemberYes = document.querySelector('#indian_tribe_member_yes')
    const tribalState = this.TribalStateTarget
    const tribalName = this.TribalNameTarget
    if (indianTribeMemberYes?.checked) {
      if (this.hasTribalStateTarget && tribalState.value == "") {
        tribalState.setCustomValidity("Tribal state is required when native american / alaska native is selected")
        tribalState.reportValidity()
        return false
      } else {
        tribalState.setCustomValidity("")
        tribalState.reportValidity()
        return this.checkTribalNameOrCodeValidation()
      }
    } else {
      tribalState.setCustomValidity("")
      tribalState.reportValidity()
      if (tribalName) {
        tribalName.setCustomValidity("")
        tribalName.reportValidity()
      }
      return true
    }
  }

  checkTribalNameOrCodeValidation() {
    const enrollStateAbbr = this.element.querySelector('#enroll_state_abbr').value
    const isFeaturedTribesEnabled = this.element.querySelector('#is_featured_tribes_selection_enabled').value === 'true'
    const selectedState = this.TribalStateTarget?.value
    if (isFeaturedTribesEnabled && selectedState === enrollStateAbbr) {
      return this.checkTribalCodeValidation()
    } else {
      return this.checkTribalNameValidation()
    }
  }

  checkTribalNameValidation() {
    // On the accessible path errors are shown inline at submit, not via native tooltips while typing —
    // just clear any existing inline error as the user edits.
    if (this.#accessibleValidationEnabled()) {
      if (this.hasTribalNameTarget) removeFieldErrorState(this.TribalNameTarget)
      return true
    }
    if (this.hasTribalNameTarget) {
      const tribalName = this.TribalNameTarget.value
      if (tribalName.trim() === "") {
        this.TribalNameTarget.setCustomValidity("Tribal name is required when native american / alaska native is selected")
        this.TribalNameTarget.reportValidity()
        return false
      } else {
        this.TribalNameTarget.setCustomValidity("")
        this.TribalNameTarget.reportValidity()
        return true
      }
    } else {
      return true
    }
  }
  checkTribalCodeValidation() {
    if (this.hasFeaturedTribeContainerTarget) {
      const featuredTribesContainer = this.FeaturedTribeContainerTarget
      let tribalCodes = featuredTribesContainer.querySelectorAll('.tribe_codes')
      let tribalIds = [...featuredTribesContainer.querySelectorAll('.tribe_codes:checked')].map(option => option.value)
      if (tribalIds.length == 0) {
        tribalCodes.forEach(code => {
          code.classList.add("indicate-invalid")
          if (code.value == "OT") {
            code.setCustomValidity("Tribal name is required when native american / alaska native is selected")
            code.reportValidity()
          }
        })
        return false
      } else if (tribalIds.includes("OT") && this.hasTribalNameTarget && this.TribalNameTarget.value.trim() === "") {
        this.TribalNameTarget.setCustomValidity("Tribal name is required when native american / alaska native is selected")
        this.TribalNameTarget.reportValidity()
        return false
      } else {
        tribalCodes.forEach(code => {
          code.classList.remove("indicate-invalid")
          code.setCustomValidity("")
          code.reportValidity()
        })
        if (this.hasTribalNameTarget) {
          this.TribalNameTarget.setCustomValidity("")
          this.TribalNameTarget.reportValidity()
        }
        featuredTribesContainer.setCustomValidity("")
        featuredTribesContainer.reportValidity()
        return true
      }
    } else {
      return true
    }
  }

  sanitize(dirty) {
    const allowedTags = sanitizeHtml.defaults.allowedTags.concat([ 'input', 'label', 'select', 'option' ])

    const accessibleFormValidation = document.documentElement.dataset.accessibleFormValidation === 'true'
    const selectAttributes = [ 'name', 'id', 'class', 'style', 'data-*' ]

    // need to add 'required' to the allowed attributes for select elements to enable error state styling on form validation
    if (accessibleFormValidation) selectAttributes.push('required')

    const options = {
      allowedTags: allowedTags,
      allowedAttributes: {
        'input': [ 'type', 'name', 'id', 'value', 'placeholder', 'required', 'checked', 'disabled', 'readonly', 'class', 'style', 'data-*', 'min', 'max' ],
        'label': [ 'for' ],
        'select': selectAttributes,
        'option': [ 'value', 'selected' ],
        'div': [ 'data-*', 'class', 'id' ],
        'i': [ 'class' ],
        'h4': [ 'class' ],
        'template': [ 'id', 'data-*' ]
      }
    }

    // adds alt and value to the allowed attributes for all elements when accessible form validation is enabled
    // without this, "SELECT STATE" becomes the default "value" for the select element and isn't blank, so doesn't get caught by the required validation
    if (accessibleFormValidation) options.allowedEmptyAttributes = [ 'alt', 'value' ]

    return sanitizeHtml(dirty, options);
  }
}
